// Client-side image downscaling for the attachment upload path.
//
// Uploads go up as base64-in-JSON, and Vercel rejects request bodies over
// ~4.5 MB with a 413 before the request ever reaches Flask. Base64 inflates
// bytes ~33%, so the real ceiling is ~3.3 MB of original image — well under the
// 10 MB the UI advertises. Rather than cap users low, we downscale large images
// in the browser so the encoded body lands comfortably under the platform
// limit while keeping plenty of resolution for site/receipt photos.
//
// (Day-2 plan: move uploads to a direct-to-Supabase signed URL, which removes
// the Vercel body limit entirely and lets us keep full-res originals.)

/** Longest-edge cap for a downscaled image. 2048px keeps signage/serial-number
 *  detail legible while shrinking phone photos dramatically. */
const MAX_EDGE = 2048;

/** Target ceiling for the *encoded* image bytes. The base64 body is ~1.37× this
 *  (base64 + JSON wrapper), so 3 MB → ~4.1 MB body, safely under Vercel's
 *  ~4.5 MB request cap. */
const SAFE_BYTES = 3 * 1024 * 1024;

export interface PreparedUpload {
  /** data: URL (base64) ready to POST as `data_base64`. */
  dataUrl: string;
  /** MIME type of the prepared bytes (image/jpeg when re-encoded). */
  fileType: string;
  /** File name, extension swapped to .jpg when re-encoded. */
  name: string;
}

/** Read a Blob/File as a base64 `data:` URL — used for the upload payload of
 *  files we don't re-encode (e.g. PDFs) as well as the image fallback path. */
export const readAsDataURL = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(blob);
  });

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode image'));
    };
    img.src = url;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas export failed'))),
      'image/jpeg',
      quality,
    );
  });

const drawScaled = async (
  img: HTMLImageElement,
  scale: number,
  quality: number,
): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvasToBlob(canvas, quality);
};

const swapToJpgExt = (name: string): string =>
  /\.jpe?g$/i.test(name) ? name : name.replace(/\.[^.]+$/, '') + '.jpg';

/**
 * Prepare an image for upload: returns the original untouched when it's already
 * small enough, otherwise re-encodes a downscaled JPEG that fits under the
 * platform body cap. Tightens quality, then dimensions, until the bytes fit (or
 * a floor is reached — a final best-effort attempt still uploads).
 *
 * Assumes `file` is one of the allowed image MIME types (caller validates).
 */
export async function prepareImageUpload(file: File): Promise<PreparedUpload> {
  let img: HTMLImageElement;
  try {
    img = await loadImage(file);
  } catch {
    // If decoding fails (corrupt/odd image), fall back to the raw bytes and let
    // the server be the judge rather than blocking the upload outright.
    return { dataUrl: await readAsDataURL(file), fileType: file.type, name: file.name };
  }

  const longest = Math.max(img.width, img.height);
  const withinDimensions = longest <= MAX_EDGE;
  const withinBytes = file.size <= SAFE_BYTES;
  // Nothing to gain — keep the original bytes and format (preserves PNG/WebP
  // and avoids a needless lossy re-encode of an already-small image).
  if (withinDimensions && withinBytes) {
    return { dataUrl: await readAsDataURL(file), fileType: file.type, name: file.name };
  }

  let scale = withinDimensions ? 1 : MAX_EDGE / longest;
  let quality = 0.85;
  let blob: Blob | null = null;
  // Up to 4 attempts: drop quality first (cheap, preserves framing), then
  // shrink dimensions further. The last attempt uploads whatever we have.
  for (let attempt = 0; attempt < 4; attempt += 1) {
    blob = await drawScaled(img, scale, quality);
    if (blob.size <= SAFE_BYTES) break;
    if (quality > 0.6) quality -= 0.15;
    else scale *= 0.8;
  }

  const finalBlob = blob!;
  return {
    dataUrl: await readAsDataURL(finalBlob),
    fileType: 'image/jpeg',
    name: swapToJpgExt(file.name || 'photo.jpg'),
  };
}
